import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import ViewListOutlined from "@mui/icons-material/ViewListOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import { grey } from "@mui/material/colors";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import PropTypes from "prop-types";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import SearchInput from "../../../../components/inputs/SearchInput";
import Flx from "../../../../components/layout/Flx";
import ScreenContent from "../../../../components/layout/ScreenContent";
import LoadingFallback from "../../../../components/LoadingFallback";
import Txt from "../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { sidebarTypeToggle } from "../../../../redux/actions/sidebarActions";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";

import LoanSizerFilesTable from "../../../../shared/tables/LoanSizerFilesTable/LoanSizerFilesTable";

const LoanDrilldownSizerTab = ({ value, tab }) => {
  const loanDrilldown = useSelector((state) => state?.loanDrilldown);
  const [rowData, setRowData] = useState(null);
  const { getAllSizerUploads, getAllSizerUploadsByDealId } = useLoanFilesHook();

  const [quickFilter, setQuickFilter] = useState(null);

  useEffect(() => {
    if (isNil(rowData)) {
      getAllSizerUploadsByDealId({
        // data: { loanId: loanDrilldown?._id },
        dealId: loanDrilldown?._id,
        onSuccessFn: (data) => {
          setRowData(data);
        },
        onFailFn: () => {
          getAllSizerUploads({
            onSuccessFn: (data) => {
              setRowData(data);
            },
            onFailFn: (error) => {
              console.error("Error fetching sizer uploads:", error);
            },
          });
          // console.error("Error fetching sizer uploads:", error);
        },
      });
    }
  }, []);

  return (
    <LoanDrilldownTabPanel value={value} tab={tab}>
      <ScreenContent>
        {isNil(rowData) ? (
          <LoadingFallback />
        ) : (
          <Card
            sx={{
              p: 0,
            }}
          >
            <FileTableCardHeader
              quickFilter={quickFilter}
              setQuickFilter={setQuickFilter}
            />
            <LoanFilesTableContent rowData={rowData} />
          </Card>
        )}
      </ScreenContent>
    </LoanDrilldownTabPanel>
  );
};

const LoanFilesTableContent = ({ quickFilter, rowData }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  // More local state

  const filteredRows = useMemo(() => {
    return rowData?.filter((row) => {
      return row?.dealId === loanDrilldown?._id;
    });
  }, [rowData, loanDrilldown]);

  if (isEmpty(filteredRows)) {
    return (
      <Box sx={{ p: 2 }}>
        <Txt>No sizers uploaded.</Txt>
      </Box>
    );
  }
  return (
    <Suspense fallback={<LoadingFallback />}>
      <LoanSizerFilesTable
        rowData={rowData}
        autoHeight
        quickFilter={quickFilter}
      />
    </Suspense>
  );
};

const FileTableCardHeader = ({ quickFilter, setQuickFilter }) => {
  const dispatch = useDispatch();
  const rootStyles = useMemo(() => {
    return {
      p: 1,
      borderBottom: `1px solid ${grey[300]}`,
      display: "flex",
      alignItems: "center",
      gap: 2,
      flexWrap: "wrap",
      position: "sticky",
      top: 0,
      left: 0,
    };
  }, []);

  return (
    <Box sx={rootStyles}>
      <Flx fw gap={1} ac wrap sx={{ flex: 1, minWidth: 250 }}>
        <SearchInput value={quickFilter} onChange={setQuickFilter} />
      </Flx>
      <Button
        size="medium"
        variant="outlined"
        onClick={() =>
          dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.SIZER_UPLOAD))
        }
        startIcon={<FileUploadOutlined className="thin" />}
      >
        Upload Sizer
      </Button>
    </Box>
  );
};

const LayoutToggle = ({ value, onChange, editing, hiddenCount }) => {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      disabled={editing}
      onChange={onChange}
      size="small"
      sx={{
        "& .MuiToggleButton-root": {
          textTransform: "none",
          fontSize: "12px",
          px: 1.5,
        },
      }}
    >
      <ToggleButton value="all">
        <ViewListOutlined className="thin" sx={{ fontSize: "16px", mr: 0.5 }} />
        All Files
      </ToggleButton>
      <ToggleButton value="group">
        <AccountTreeOutlined
          className="thin"
          sx={{ fontSize: "16px", mr: 0.5 }}
        />
        Group By Category
      </ToggleButton>
      {hiddenCount > 0 && (
        <ToggleButton value="hidden">
          <VisibilityOffOutlined
            className="thin"
            sx={{ fontSize: "16px", mr: 0.5 }}
          />
          Hidden Files ({hiddenCount})
        </ToggleButton>
      )}
    </ToggleButtonGroup>
  );
};

LoanDrilldownSizerTab.propTypes = {
  value: PropTypes.any,
  tab: PropTypes.any,
};

LoanFilesTableContent.propTypes = {
  files: PropTypes.array,
  hiddenCount: PropTypes.number,
  layoutMode: PropTypes.string,
  setLayoutMode: PropTypes.func,
};

FileTableCardHeader.propTypes = {
  quickFilter: PropTypes.string,
  setQuickFilter: PropTypes.func,
  layoutMode: PropTypes.string,
  onLayoutChange: PropTypes.func,
  onUpdateClick: PropTypes.func,
  loading: PropTypes.bool,
  changedRowsCount: PropTypes.number,
  hiddenCount: PropTypes.number,
  editing: PropTypes.bool,
  onEditCancel: PropTypes.func,
  onEditClick: PropTypes.func,
  changedRows: PropTypes.array,
};

LayoutToggle.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  editing: PropTypes.bool,
  hiddenCount: PropTypes.number,
};

export default LoanDrilldownSizerTab;
